// Knowledge (design §10): the workspace's and projects' knowledge base.
// `/knowledge` shows the overview, `/knowledge/<space>/<path>` a page
// (`…/x.md`) or a folder (anything else); `?view=edit|history|new` picks the
// page mode and `/knowledge?view=proposals` the proposals inbox.

import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  KnowledgeBrowser,
  type KbLocation,
  type KbView,
} from '../components/knowledge/knowledge-browser'
import { PageHeader } from '../components/ui'

export interface KnowledgeSearch {
  view?: KbView
}

const VIEWS: KbView[] = ['page', 'edit', 'history', 'new', 'proposals']

export function validateKnowledgeSearch(search: Record<string, unknown>): KnowledgeSearch {
  return typeof search.view === 'string' && VIEWS.includes(search.view as KbView)
    ? { view: search.view as KbView }
    : {}
}

export function KnowledgePage() {
  const params = useParams({ strict: false }) as { space?: string; _splat?: string }
  const search = useSearch({ strict: false }) as KnowledgeSearch
  const navigate = useNavigate()

  const location: KbLocation = {
    space: params.space,
    path: params._splat || undefined,
    view: search.view,
  }

  function onNavigate(next: KbLocation): void {
    const view = next.view && next.view !== 'page' ? next.view : undefined
    if (!next.space) {
      void navigate({ to: '/knowledge', search: view ? { view } : {} })
    } else if (!next.path) {
      void navigate({
        to: '/knowledge/$space',
        params: { space: next.space },
        search: view ? { view } : {},
      })
    } else {
      void navigate({
        to: '/knowledge/$space/$',
        params: { space: next.space, _splat: next.path },
        search: view ? { view } : {},
      })
    }
  }

  return (
    <div>
      <PageHeader
        title="Knowledge"
        description="What people and agents know about the workspace and its projects. Agents read it before they work and keep it up to date."
      />
      <KnowledgeBrowser location={location} onNavigate={onNavigate} />
    </div>
  )
}
